import React, {useMemo} from "react";
import {Box, styled, Typography} from "@mui/material";
import {Title} from "./AboutSection";
import {useTranslation} from "react-i18next";
import {AccessTimeRounded, LocationOnOutlined} from "@mui/icons-material";
import {useInView} from "react-intersection-observer";
import {animated, useSpring} from "@react-spring/web";
import useScrollDirection from "../hooks/useScrollDirection.hook";
import useIsMobile from "../hooks/useIsMobile.hook";

const Image = styled('img')(() => ({
  height: 'min-content',
}));

const Links = styled(Box)(({theme}) => ({
  gap: 48,
  [theme.breakpoints.up('xs')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const BranchesContainer = styled(Box)(({theme}) => ({
  display: 'grid',
  padding: '40px 0',
  marginTop: 40,
  justifyItems: 'center',
  [theme.breakpoints.up('xs')]: {
    gridTemplateColumns: '1fr',
    gap: 80,
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 32,
  },
}));

const BranchItem = styled(Box)(({theme}) => ({
  maxWidth: 200,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const AnimatedBranchItem = animated(BranchItem);
const BranchComponent = ({name, date, address, delay}) => {
  const [ref, inView, scrollEntry] = useInView({triggerOnce: false, threshold: 0.1});
  const isScrollDown = useScrollDirection();
  const isMobile = useIsMobile();

  const scrollDown = useMemo(() => isScrollDown(scrollEntry), [scrollEntry]);

  const transformAxis = isMobile ? 'X' : 'Y';

  const animation = useSpring({
    from: {opacity: 0, transform: scrollDown ? `translate${transformAxis}(100%)` : `translate${transformAxis}(-100%)`},
    to: {
      opacity: inView ? 1 : 0,
      transform: inView ? `translate${transformAxis}(0%)` : scrollDown ? `translate${transformAxis}(100%)` : `translate${transformAxis}(-100%)`
    },
    config: {duration: 200},
    delay,
    reset: true,
  });

  return (
    <AnimatedBranchItem ref={ref} style={animation}>
      <Typography paddingBottom={1} variant="h6" fontWeight={600} letterSpacing={1}>{name}</Typography>
      <Box display="flex" gap={1}>
        <AccessTimeRounded sx={{fontSize: 24, color: '#000'}}/>
        <Typography variant="subtitle1" letterSpacing={0.5}>
          {date}
        </Typography>
      </Box>
      <Box display="flex" gap={1}>
        <LocationOnOutlined sx={{fontSize: 24, color: '#000'}}/>
        <Typography variant="body1" letterSpacing={0.5}>
          {address}
        </Typography>
      </Box>
    </AnimatedBranchItem>
  );
};

const FillialsSection = () => {
  const {t} = useTranslation();

  return (
    <Box id="fillials" sx={{mt: 4, pb: 10, p: 2}} display="flex" flexDirection="column"
         alignItems="center">
      <Box maxWidth={1200} width="100%">
        <Title variant="h4">{t('fillials.title')}</Title>
        <BranchesContainer>
          <BranchComponent
            name="Bochum"
            date={`${t('sundays')} 10:00`} address="Harpener Heide 9 44805 Bochum"
            delay={0}
          />
          <BranchComponent
            name="Münster"
            date={`${t('sundays')} 14:00`}
            address="Zum Erlenbusch 15 48167 Münster"
            delay={50}
          />
          <BranchComponent
            name="Wuppertal"
            date={`${t('sundays')} 10:00`}
            address="Otto-Hausmann-Ring 184 42115 Wuppertal"
            delay={100}
          />
          <BranchComponent
            name="Lünen"
            date={`${t('sundays')} 10:00`} address="Am Friedhof 18 44532 Lünen"
            delay={150}
          />
          <BranchComponent
            name="Unna"
            date={`${t('sundays')} 10:00`} address="Hansastraße 87d 59425 Unna"
            delay={200}
          />
          <BranchComponent
            name="Lüdinghausen"
            date={`${t('sundays')} 10:00`} address="An der Vogelrute 5 59348 Lüdinghausen"
            delay={250}
          />
        </BranchesContainer>


        <Links sx={{mt: 12, mb: 8}} display="flex" justifyContent="space-between" alignItems="center">
          <a href="https://helping-hands-scm.de" target="_blank">
            <Image src="img/helping-hands.png"/>
          </a>
          <a href="https://www.bfp.de" target="_blank">
            <Image src="img/bfp.png"/>
          </a>
          <a href="https://www.youversion.com" target="_blank">
            <Image src="img/youversion.png"/>
          </a>
        </Links>
      </Box>
    </Box>
  );
};

export default FillialsSection;
